import { useSearchParams } from 'react-router-dom'
import { startOfDay, subDays } from 'date-fns'

/**
 * Ventana de tiempo de la columna Finalizada (CLAUDE.md §6, fase 7). Vive en
 * la URL (`?done=`) como el resto de filtros; sin parámetro, 7 días.
 */
export const DONE_WINDOWS = ['7d', '30d', 'all'] as const
export type DoneWindow = (typeof DONE_WINDOWS)[number]
export const DEFAULT_DONE_WINDOW: DoneWindow = '7d'

const WINDOW_DAYS = { '7d': 7, '30d': 30 } as const

export function parseDoneWindow(raw: string | null): DoneWindow {
  return DONE_WINDOWS.find((candidate) => candidate === raw) ?? DEFAULT_DONE_WINDOW
}

/**
 * `doneSince` para `GET /tasks`: el inicio del día local de hace N-1 días, así
 * que "7 días" es hoy y los seis anteriores. Redondeado al día a propósito: la
 * marca forma parte de la clave de TanStack Query, y con la hora exacta
 * cambiaría en cada render y no se reutilizaría nunca la caché.
 */
export function doneSinceFor(doneWindow: DoneWindow, now: Date = new Date()): string | undefined {
  if (doneWindow === 'all') return undefined
  return startOfDay(subDays(now, WINDOW_DAYS[doneWindow] - 1)).toISOString()
}

export function useDoneWindow() {
  const [searchParams, setSearchParams] = useSearchParams()
  const doneWindow = parseDoneWindow(searchParams.get('done'))

  function setDoneWindow(next: DoneWindow): void {
    setSearchParams(
      (current) => {
        const params = new URLSearchParams(current)
        if (next === DEFAULT_DONE_WINDOW) params.delete('done')
        else params.set('done', next)
        return params
      },
      { replace: true },
    )
  }

  return { doneWindow, doneSince: doneSinceFor(doneWindow), setDoneWindow }
}
